"use client";

import { useEffect } from "react";
import { isStaleChunkError } from "@/lib/utils";
import { STALE_CHUNK_RELOAD_FLAG_KEY, STALE_CHUNK_GUARD_RESET_MS } from "@/lib/constants";

/**
 * A deployed build's JS chunk filenames are content-hashed — a tab left open
 * across a new deploy still holds the *old* filenames, so a client-side
 * navigation (e.g. the wizard's step rail) tries to fetch a chunk that no
 * longer exists on the server. That fetch fails silently: no error UI, the
 * navigation just never completes. This reloads once to pick up the new
 * build instead of leaving the user stuck. Session-scoped guard against a
 * refresh loop if reloading somehow doesn't clear the failure.
 */
export function StaleChunkReloader() {
  useEffect(() => {
    // Clear the guard once this page has run stably for a bit — otherwise a
    // second, later deploy while the tab stays open would be permanently
    // blocked from ever reloading again.
    const clearGuardTimer = setTimeout(
      () => sessionStorage.removeItem(STALE_CHUNK_RELOAD_FLAG_KEY),
      STALE_CHUNK_GUARD_RESET_MS,
    );

    function reloadOnce() {
      if (sessionStorage.getItem(STALE_CHUNK_RELOAD_FLAG_KEY)) return;
      sessionStorage.setItem(STALE_CHUNK_RELOAD_FLAG_KEY, "1");
      window.location.reload();
    }

    function handleRejection(event: PromiseRejectionEvent) {
      const message = event.reason instanceof Error ? event.reason.message : String(event.reason);
      if (isStaleChunkError(message)) reloadOnce();
    }

    function handleError(event: ErrorEvent) {
      if (isStaleChunkError(event.message)) reloadOnce();
    }

    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);
    return () => {
      clearTimeout(clearGuardTimer);
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  return null;
}
